import type { DialogProps } from '@mui/material/Dialog';
import type { SxProps, Theme } from '@mui/material/styles';

import { useCallback, useEffect, useState } from 'react';

import {
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Search } from 'lucide-react';
import { Iconify } from 'src/components/iconify';
import { Label } from 'src/components/label';
import { useAgentPagination } from 'src/data/hooks/pagination-data/use-agent-pagination';
import type { Agent } from 'src/data/models/agent.model';
import { PROVINCES } from 'src/utils/consts';

// ----------------------------------------------------------------------

export type Props = DialogProps & { onSubmit?: (agent: Agent) => void };

export function DialogAgentPicker({
  open,
  onClose,
  onSubmit,
  /********/
  PaperProps,
  title = 'Dialog Title',
  ...other
}: Props) {
  const theme = useTheme();
  const handleSubmit = useCallback(
    (agent: Agent) => {
      onSubmit?.(agent);
      onClose?.({}, 'backdropClick');
    },
    [onSubmit]
  );

  const agentData = useAgentPagination({
    loadingOnInit: false,
  });

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [province, setProvince] = useState('');

  useEffect(() => {
    if (!open) {
      setProvince('');
      setSearch('');
      agentData.clear();
    }
  }, [open]);

  useEffect(() => {
    if (province) {
      agentData.updateParams({
        search,
        cityName: province,
        skip: 1,
      });
    } else {
      agentData.clear();
    }
  }, [province]);

  useEffect(() => {
    if (!search) {
      agentData.clear();
    }
  }, [search]);

  const contentStyles: SxProps<Theme> = {
    py: 1,
    gap: 3,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        ...PaperProps,
        sx: [...(Array.isArray(PaperProps?.sx) ? (PaperProps?.sx ?? []) : [PaperProps?.sx])],
      }}
      {...other}
    >
      <DialogTitle sx={{ pb: 2 }}>{title}</DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <Box sx={contentStyles}>
          <TextField
            variant="outlined"
            size="medium"
            placeholder="Masukkan nama lokasi..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter')
                agentData.updateParams({
                  search,
                  skip: 1,
                });
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="start">
                  <IconButton onClick={() => setSearch('')}>
                    <Iconify icon="carbon:close-filled" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {/* {(!search || (!agentData.loading && agentData.data.length <= 0)) && (
            <div className="flex flex-col gap-[10px]">
              <Typography variant="subtitle2">Rekomendasi</Typography>
              <div className="flex flex-row flex-wrap gap-[10px]">
                {recommendationCities.map((e, i) => (
                  <Button
                    variant="outlined"
                    key={i}
                    onClick={() => {
                      setSearch(e);
                      agentData.updateParams({ search: e, skip: 1 });
                    }}
                  >
                    {e}
                  </Button>
                ))}
              </div>
            </div>
          )} */}
          {!province && !loading && agentData.data.length <= 0 && (
            <div className="flex flex-col gap-[10px]">
              <Typography variant="subtitle2">Rekomendasi</Typography>
              <div className="flex flex-row flex-wrap gap-[10px]">
                {PROVINCES.map((e, i) => (
                  <Button variant="outlined" key={i} onClick={() => setProvince(e.value)}>
                    {e.name}
                  </Button>
                ))}
              </div>
            </div>
          )}
          {province && (
            <div className="flex items-center justify-between gap-[8px]">
              <div className="flex items-center gap-[8px]">
                <Typography variant="h6">{province}</Typography>
                <Label color="primary" variant="soft">
                  Provinsi
                </Label>
              </div>
              <IconButton onClick={() => setProvince('')}>
                <Iconify icon="carbon:close-filled" />
              </IconButton>
            </div>
          )}
          {agentData.data.length > 0 && (
            <div className="flex flex-col gap-[10px]">
              {/* <div className="flex items-center gap-[8px]">
                <Typography variant="h6">{search}</Typography>
                <Label color="primary" variant="soft">
                  Agen
                </Label>
              </div> */}

              <div className="flex flex-col gap-[10px]">
                {agentData.data.map((e, i) => (
                  <Box
                    onClick={() => handleSubmit(e)}
                    sx={{
                      margin: '0 -8px',
                      cursor: 'pointer',
                      '&:hover': {
                        borderRadius: '6px',
                        background: 'var(--p-8, rgba(0, 167, 111, 0.08))',
                      },
                    }}
                    key={i}
                  >
                    <div className="py-[4px] px-[8px]">
                      <Typography variant="subtitle2">{e.name}</Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.vars.palette.text.secondary,
                        }}
                      >
                        {e.address}
                      </Typography>
                      {e.phone?.trim() ? (
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.vars.palette.text.secondary,
                          }}
                        >
                          {e.phone}
                        </Typography>
                      ) : null}
                    </div>
                  </Box>
                ))}
              </div>
            </div>
          )}

          {agentData.loading && (
            <div className="flex justify-center w-full">
              <CircularProgress size={20} />
            </div>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
